/**
 * Pure helpers for the print preview: fit-to-page scale and page-break offsets.
 * No React, no DOM, so they can be unit tested directly.
 */

/** Smallest scale fit-to-page may apply before it gives up and paginates. */
export const FIT_TO_PAGE_FLOOR = 0.7;
/** Fraction of the usable page height a fitted section may fill (rounding epsilon). */
export const FIT_TO_PAGE_TARGET = 0.97;

export function computeFitScale(args: {
  naturalWidthPx: number;
  innerWidthPx: number;
  sectionHeightsPx: number[];
  usableHeightPx: number;
  fitToPage: boolean;
  floor?: number;
  target?: number;
}): number {
  const {
    naturalWidthPx,
    innerWidthPx,
    sectionHeightsPx,
    usableHeightPx,
    fitToPage,
    floor = FIT_TO_PAGE_FLOOR,
    target = FIT_TO_PAGE_TARGET,
  } = args;

  const widthScale = naturalWidthPx > innerWidthPx ? innerWidthPx / naturalWidthPx : 1;
  if (!fitToPage || sectionHeightsPx.length === 0) return widthScale;

  const tallest = Math.max(...sectionHeightsPx);
  if (!(tallest > 0)) return widthScale;

  const heightScale = (usableHeightPx * target) / tallest;
  const candidate = Math.min(widthScale, heightScale, 1);
  return candidate >= floor ? candidate : widthScale;
}

export function computePageOffsets(args: {
  candidatesPx: number[];
  sectionTopsPx: number[];
  totalPx: number;
  usablePx: number;
  preferSectionBreaks: boolean;
}): number[] {
  const { sectionTopsPx, totalPx, usablePx, preferSectionBreaks } = args;
  const candidates = [...args.candidatesPx].sort((a, b) => a - b);

  // Greedy snap: pick the largest candidate <= cursor + usablePx, but
  // require pages to be at least 60% full so we don't break tiny.
  const offsets: number[] = [0];
  let cursor = 0;
  const minFill = usablePx * 0.6;
  while (cursor + usablePx < totalPx) {
    const target = cursor + usablePx;
    let next = -1;
    if (preferSectionBreaks) {
      for (const s of sectionTopsPx) {
        if (s > cursor && s <= target && s > next) next = s;
      }
    }
    if (next <= cursor) {
      let best = -1;
      for (const c of candidates) {
        if (c >= cursor + minFill && c <= target) best = c;
      }
      next = best > 0 ? best : target;
    }
    if (next <= cursor) break;
    offsets.push(next);
    cursor = next;
  }
  return offsets;
}
